import { useMemo, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Check, LibraryBig, Plus, Search, Tags } from "lucide-react";
import { Card, InlineNotice, PageHeader, StatusBadge } from "../components/ui";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db, type CachedProduct } from "../lib/db";
import { refreshFromServer } from "../lib/data";
import { BUILTIN_CATALOG, BUILTIN_VERTICALS, builtinCategories } from "../lib/builtin-catalog";

const PAGE_SIZE = 80;
const MAX_SELECTION = 500;

export function ProductLibraryPage() {
  const auth = useAuth();
  const role = auth.session?.user.role ?? "OWNER";
  const canManage = ["OWNER", "MANAGER"].includes(role);
  const products = useLiveQuery(() => db.products.toArray(), [], []);
  const [query, setQuery] = useState("");
  const [vertical, setVertical] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const existingSkus = useMemo(
    () => new Set(products.map((item) => item.sku.toUpperCase())),
    [products],
  );
  const categories = useMemo(() => builtinCategories(vertical || undefined), [vertical]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("vi");
    return BUILTIN_CATALOG.filter((item) => {
      if (vertical && item.businessVertical !== vertical) return false;
      if (category && item.category !== category) return false;
      if (!needle) return true;
      return [item.sku, item.name, item.businessVertical, item.category, item.subcategory]
        .some((value) => value.toLocaleLowerCase("vi").includes(needle));
    });
  }, [query, vertical, category]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function changeVertical(value: string) {
    setVertical(value);
    setCategory("");
    setPage(1);
  }

  function toggle(sku: string) {
    setError("");
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(sku)) next.delete(sku);
      else if (next.size < MAX_SELECTION) next.add(sku);
      return next;
    });
  }

  function selectVisible() {
    setSelected((current) => {
      const next = new Set(current);
      for (const item of visible) {
        if (next.size >= MAX_SELECTION) break;
        if (!existingSkus.has(item.sku.toUpperCase())) next.add(item.sku);
      }
      return next;
    });
  }

  async function activateSelected() {
    if (!canManage || selected.size === 0) return;
    if (!auth.demo && !navigator.onLine) {
      setError("Cần kết nối mạng để thêm SKU vào cửa hàng và kiểm tra quota/trùng mã.");
      return;
    }

    const items = BUILTIN_CATALOG.filter((item) => selected.has(item.sku));
    setBusy(true);
    setError("");
    setNotice("");

    try {
      if (auth.demo) {
        const localRows: CachedProduct[] = items.map((item) => ({
          id: crypto.randomUUID(),
          name: item.name,
          sku: item.sku,
          barcode: null,
          baseUnit: item.baseUnit,
          businessVertical: item.businessVertical,
          category: item.category,
          subcategory: item.subcategory,
          salePrice: 0,
          avgCost: 0,
          stockQty: 0,
          minStockQty: 0,
          trackExpiry: item.trackExpiry,
          nextExpiryDate: null,
          units: [],
        }));
        await db.products.bulkAdd(localRows);
      } else {
        await api("/imports/batch-products", {
          method: "POST",
          body: JSON.stringify({
            products: items.map((item) => ({
              name: item.name,
              sku: item.sku,
              barcode: null,
              baseUnit: item.baseUnit,
              businessVertical: item.businessVertical,
              category: item.category,
              subcategory: item.subcategory,
              salePrice: 0,
              openingCost: 0,
              openingQty: 0,
              minStockQty: 0,
              trackExpiry: item.trackExpiry,
              openingLotCode: null,
              openingExpiryDate: null,
              units: [],
            })),
          }),
        });
        await refreshFromServer();
      }

      setSelected(new Set());
      setNotice(`Đã thêm ${items.length} SKU vào Danh mục sản phẩm. Tồn kho vẫn bằng 0 cho đến khi nhập hàng.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể thêm SKU từ thư viện.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        title="Thư viện 5.000 SKU"
        description="Kho mẫu Product Master theo ngành và danh mục. Chỉ SKU được chọn mới được thêm vào cửa hàng; không phát sinh tồn kho, giá vốn hay mã vạch giả."
        back
        action={canManage ? (
          <button className="btn btn-primary" disabled={busy || selected.size === 0} onClick={() => void activateSelected()}>
            <Plus size={18} /> {busy ? "Đang thêm..." : `Thêm ${selected.size} SKU`}
          </button>
        ) : undefined}
      />

      <InlineNotice tone="info" title="Catalog mẫu, không phải cơ sở dữ liệu barcode thương mại">
        SKU BL-* là mã nội bộ do BLedger tạo để làm mẫu. Giá bán mặc định 0 ₫, barcode để trống và tồn kho = 0.
      </InlineNotice>

      {notice ? <InlineNotice tone="success" title={notice} /> : null}
      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <div className="dashboard-grid">
        <Card><small>Tổng mẫu sẵn có</small><strong>{BUILTIN_CATALOG.length.toLocaleString("vi-VN")}</strong></Card>
        <Card><small>Ngành kinh doanh</small><strong>{BUILTIN_VERTICALS.length}</strong></Card>
        <Card><small>Đã có trong cửa hàng</small><strong>{products.filter((p) => p.sku.startsWith("BL-")).length}</strong></Card>
      </div>

      <div className="search-box">
        <Search size={20} />
        <input
          value={query}
          onChange={(event) => { setQuery(event.target.value); setPage(1); }}
          placeholder="Tìm SKU, tên hàng, ngành hoặc danh mục"
        />
      </div>

      <div className="form-grid">
        <label>
          Ngành kinh doanh
          <select value={vertical} onChange={(event) => changeVertical(event.target.value)}>
            <option value="">Tất cả ngành</option>
            {BUILTIN_VERTICALS.map((item) => <option key={item.code} value={item.name}>{item.name}</option>)}
          </select>
        </label>
        <label>
          Danh mục
          <select value={category} onChange={(event) => { setCategory(event.target.value); setPage(1); }}>
            <option value="">Tất cả danh mục</option>
            {categories.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
      </div>

      <Card>
        <div className="section-title">
          <div><LibraryBig size={20} /><h2>{filtered.length.toLocaleString("vi-VN")} SKU phù hợp</h2></div>
          {canManage ? (
            <div className="header-actions">
              <button className="btn btn-secondary compact" type="button" onClick={selectVisible}>Chọn trang</button>
              <button className="btn btn-secondary compact" type="button" onClick={() => setSelected(new Set())}>Bỏ chọn</button>
            </div>
          ) : null}
        </div>
        <small>Tối đa {MAX_SELECTION} SKU mỗi lần để giữ import all-or-nothing và phù hợp giới hạn batch.</small>
      </Card>

      <div className="product-list">
        {visible.map((item) => {
          const exists = existingSkus.has(item.sku.toUpperCase());
          const checked = selected.has(item.sku);
          return (
            <Card key={item.sku} className="inventory-row inventory-row-rich">
              <div className="product-avatar"><Tags size={18} /></div>
              <div className="product-main">
                <strong>{item.name}</strong>
                <div className="product-meta"><span>SKU <b>{item.sku}</b></span><span>ĐVT <b>{item.baseUnit}</b></span></div>
                <div className="inventory-badges">
                  <StatusBadge tone="info">{item.businessVertical}</StatusBadge>
                  <StatusBadge tone="neutral">{item.category}</StatusBadge>
                  <StatusBadge tone="neutral">{item.subcategory}</StatusBadge>
                  {item.trackExpiry ? <StatusBadge tone="warning">Theo dõi HSD</StatusBadge> : null}
                  {exists ? <StatusBadge tone="success">Đã có</StatusBadge> : null}
                </div>
              </div>
              {canManage ? (
                <button
                  type="button"
                  className={checked ? "btn btn-primary compact" : "btn btn-secondary compact"}
                  disabled={exists || (!checked && selected.size >= MAX_SELECTION)}
                  onClick={() => toggle(item.sku)}
                >
                  {checked ? <Check size={17} /> : <Plus size={17} />}
                  {exists ? "Đã thêm" : checked ? "Đã chọn" : "Chọn"}
                </button>
              ) : null}
            </Card>
          );
        })}
      </div>

      <div className="header-actions" style={{ justifyContent: "space-between" }}>
        <button className="btn btn-secondary" disabled={safePage <= 1} onClick={() => setPage(Math.max(1, safePage - 1))}>← Trước</button>
        <span>Trang {safePage}/{pageCount}</span>
        <button className="btn btn-secondary" disabled={safePage >= pageCount} onClick={() => setPage(Math.min(pageCount, safePage + 1))}>Sau →</button>
      </div>
    </div>
  );
}
